import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTranslation } from '@/contexts/I18nContext';
import { Smartphone, RefreshCw, Settings } from 'lucide-react';

interface IOSMicrophoneGuideProps {
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
}

export const IOSMicrophoneGuide: React.FC<IOSMicrophoneGuideProps> = ({
  onRetry,
  onDismiss,
  className = ''
}) => {
  const { t } = useTranslation();

  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           // @ts-ignore - platform is deprecated but still needed for iOS detection
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  const isSafari = () => {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  };

  const isChrome = () => {
    return /chrome/i.test(navigator.userAgent) && !/edge/i.test(navigator.userAgent);
  };

  const getBrowserSpecificInstructions = () => {
    if (isSafari()) {
      return [
        t('ui.safariInstructions'),
        '或者：设置 → Safari → 网站设置 → 麦克风 → 允许',
        '刷新页面后重新尝试'
      ];
    } else if (isChrome()) {
      return [
        '点击地址栏左侧的 🔒 图标',
        '选择"网站设置"或"权限"',
        '将麦克风设置为"允许"',
        '刷新页面后重新尝试'
      ];
    } else {
      return [
        t('ui.genericInstructions'),
        '刷新页面后重新尝试'
      ];
    }
  };

  if (!isIOS()) {
    return null; // Only show on iOS
  }

  return (
    <Card className={`border-orange-200 bg-orange-50 ${className}`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-orange-800">
          <Smartphone className="h-5 w-5" />
          {t('ui.iosPermissionTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-orange-700">
          <p className="mb-3">
            {t('ui.iosPermissionDescription')}
          </p>
          
          <div className="space-y-2">
            {getBrowserSpecificInstructions().map((instruction, index) => (
              <div key={index} className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 bg-orange-200 text-orange-800 rounded-full text-xs flex items-center justify-center font-medium">
                  {index + 1}
                </span>
                <span>{instruction}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-orange-100 p-3 rounded-lg">
          <p className="text-xs text-orange-600">
            💡 <strong>{t('common.tip', '提示')}：</strong>{t('ui.iosPermissionTip')}
          </p>
        </div>

        <div className="flex gap-2 pt-2">
          {onRetry && (
            <Button
              onClick={onRetry}
              size="sm"
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              {t('ui.retryPermission')}
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              // Try to open settings (this may not work on all browsers)
              if ('permissions' in navigator) {
                // Modern browsers might support this
                window.open('app-settings:', '_blank');
              }
            }}
            className="flex items-center gap-2"
          >
            <Settings className="h-4 w-4" />
            {t('ui.openSystemSettings')}
          </Button>

          {onDismiss && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onDismiss}
            >
              {t('ui.dismiss')}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
